import { useMemo, type ReactNode } from "react";

import { useIssueStatuses } from "@/domains/pm/hooks/useIssueStatuses";
import type { StatusCatalogue } from "@/domains/pm/statuses/statusCatalogue";
import { StatusCatalogueContext } from "./statusCatalogueContext";

/**
 * Loads one building's status catalogue for every PM surface under it.
 *
 * A context rather than a prop, because StatusBadge is rendered from a dozen
 * places — issue rows, kanban cards, linked-issue chips, the IFC and drawing
 * issue lists, the protocol wizard — most of which have an issue and no
 * building id to thread through. A context rather than each badge calling the
 * hook, because a 200-row Gantt would otherwise rebuild the catalogue 200
 * times per render.
 */
export function StatusCatalogueProvider({
  buildingId,
  children,
}: {
  buildingId: number | undefined;
  children: ReactNode;
}) {
  const { catalogue, isLoading } = useIssueStatuses(buildingId);
  return (
    <StatusCatalogueValueProvider
      catalogue={catalogue}
      buildingId={buildingId}
      isLoading={isLoading}
    >
      {children}
    </StatusCatalogueValueProvider>
  );
}

/**
 * Publishes a catalogue that has already been built. Used by the fetching
 * provider above, and by anything holding a catalogue of its own — a preview,
 * a harness, a test — so that surface does not have to reach the network to
 * render a status.
 */
export function StatusCatalogueValueProvider({
  catalogue,
  buildingId,
  isLoading = false,
  children,
}: {
  catalogue: StatusCatalogue;
  buildingId?: number;
  isLoading?: boolean;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({ catalogue, buildingId, isLoading }),
    [catalogue, buildingId, isLoading]
  );
  return (
    <StatusCatalogueContext.Provider value={value}>{children}</StatusCatalogueContext.Provider>
  );
}
